/**
 * AbilityDisplayService - Resolves ability IDs into human-readable display data.
 *
 * This service bridges the shared AbilityRegistry with the frontend UI.
 * When a card or minion is inspected, the service looks up each ability ID
 * and returns its name, description, and trigger type for display.
 *
 * Abilities that are not registered (unknown IDs) are represented with
 * a fallback display showing the raw ID.
 */

import { AbilityDefinition, AbilityRegistry, AbilityTriggerType } from '../../shared/models/AbilityRegistry';

// ─── Display Data Interfaces ────────────────────────────────────────────────

/**
 * Display-ready data for a single ability.
 * Used by the UI to render ability name, description, and trigger type.
 */
export interface AbilityDisplayData {
  /** The stable ability ID (e.g., "lifesteal"). */
  id: string;

  /** Human-readable name shown to the player (e.g., "Lifesteal"). */
  name: string;

  /** Description of what the ability does. */
  description: string;

  /** The trigger type that fires this ability. */
  triggerType: AbilityTriggerType;

  /** Whether this ability was resolved from the registry (true) or is unknown (false). */
  isRegistered: boolean;
}

/**
 * Display data for a minion/card's full ability set.
 * Returned when a card or minion is inspected.
 */
export interface MinionAbilityDisplayData {
  /** Array of display data for each ability. */
  abilities: AbilityDisplayData[];

  /** Number of abilities (convenience field). */
  abilityCount: number;

  /** Whether the minion/card has any abilities. */
  hasAbilities: boolean;

  /** Whether all abilities are registered (no unknown IDs). */
  allAbilitiesRegistered: boolean;
}

// ─── Service ────────────────────────────────────────────────────────────────

/**
 * Service that resolves ability IDs to display-ready data using an AbilityRegistry.
 */
export class AbilityDisplayService {
  private _registry: AbilityRegistry;

  /**
   * Creates a new AbilityDisplayService.
   * @param registry The AbilityRegistry to use for lookups.
   */
  constructor(registry: AbilityRegistry) {
    this._registry = registry;
  }

  /**
   * Resolves a single ability ID to display data.
   * If the ability is not registered, returns a fallback display with the raw ID.
   * @param abilityId The ability ID to resolve.
   * @returns Display data for the ability.
   */
  resolveAbility(abilityId: string): AbilityDisplayData {
    const definition = this._registry.tryResolve(abilityId);
    if (definition) {
      return {
        id: definition.id,
        name: definition.name,
        description: definition.description,
        triggerType: definition.triggerType,
        isRegistered: true,
      };
    }

    // Fallback for unknown abilities
    return {
      id: abilityId,
      name: `Unknown (${abilityId})`,
      description: 'This ability is not registered.',
      triggerType: 'OnAttack' as AbilityTriggerType, // default placeholder
      isRegistered: false,
    };
  }

  /**
   * Resolves an array of ability IDs to display data.
   * Returns a MinionAbilityDisplayData object with all resolved abilities.
   * @param abilityIds Array of ability IDs to resolve.
   * @returns Display data for all abilities.
   */
  resolveAbilities(abilityIds: string[]): MinionAbilityDisplayData {
    const abilities: AbilityDisplayData[] = abilityIds.map((id) =>
      this.resolveAbility(id)
    );

    const allRegistered = abilities.every((a) => a.isRegistered);

    return {
      abilities,
      abilityCount: abilities.length,
      hasAbilities: abilities.length > 0,
      allAbilitiesRegistered: allRegistered,
    };
  }

  /**
   * Returns a human-readable summary of abilities for tooltip/brief display.
   * Format: "Ability1 (OnAttack), Ability2 (OnKill)"
   * @param abilityIds Array of ability IDs to summarize.
   * @returns A comma-separated summary string, or empty string if no abilities.
   */
  getAbilitySummary(abilityIds: string[]): string {
    if (abilityIds.length === 0) {
      return '';
    }

    return abilityIds
      .map((id) => {
        const display = this.resolveAbility(id);
        return `${display.name} (${display.triggerType})`;
      })
      .join(', ');
  }

  /**
   * Groups abilities by their trigger type.
   * Useful for categorizing abilities in the UI (e.g., showing OnAttack abilities separately).
   * @param abilityIds Array of ability IDs to group.
   * @returns A map from trigger type to array of display data.
   */
  groupByTriggerType(abilityIds: string[]): Map<AbilityTriggerType, AbilityDisplayData[]> {
    const groups = new Map<AbilityTriggerType, AbilityDisplayData[]>();

    for (const id of abilityIds) {
      const display = this.resolveAbility(id);
      const group = groups.get(display.triggerType) ?? [];
      group.push(display);
      groups.set(display.triggerType, group);
    }

    return groups;
  }
}
